import type { CompanyProfileMeasuresDto } from '@/api/companies'
import { formatDate } from '@/components/contacts/contactFormat'
import { timeFromNowVerbose } from '@/utils/time'

/**
 * A borderless typographic strip, not stat tiles — the same idiom the contact overview uses for
 * its measures. Every entry is conditional on having a value, so a thinly-known company shows
 * fewer numbers rather than a row of zeroes.
 */
export function CompanyMeasures({ measures }: { measures: CompanyProfileMeasuresDto }) {
  const hasAny =
    measures.PeopleCount > 0 ||
    measures.ConvertedCount > 0 ||
    measures.SignalScore > 0 ||
    measures.CampaignCount > 0 ||
    !!measures.FirstTargetedUtc ||
    !!measures.LastConversionUtc

  if (!hasAny) return null

  return (
    <div class="flex flex-wrap items-baseline gap-x-7 gap-y-2 px-5 pb-4">
      {/* Conditional like the rest: the strip is satisfied by any one measure, and leading with a
          literal "0 people" would be the row of zeroes this layout exists to avoid. */}
      {measures.PeopleCount > 0 && (
        <Measure value={measures.PeopleCount} label={measures.PeopleCount === 1 ? 'person' : 'people'} />
      )}
      {measures.ConvertedCount > 0 && <Measure value={measures.ConvertedCount} label="converted" />}
      {measures.SignalScore > 0 && <Measure value={measures.SignalScore} label="company signal" />}
      {measures.CampaignCount > 0 && (
        <Measure value={measures.CampaignCount} label={measures.CampaignCount === 1 ? 'campaign' : 'campaigns'} />
      )}
      {measures.FirstTargetedUtc && (
        <Measure quiet value={formatDate(measures.FirstTargetedUtc)} label="first targeted" />
      )}
      {measures.LastConversionUtc && (
        <Measure quiet value={timeFromNowVerbose(measures.LastConversionUtc)} label="last conversion" />
      )}
    </div>
  )
}

function Measure({ value, label, quiet = false }: { value: string | number; label: string; quiet?: boolean }) {
  return (
    <div class="flex items-baseline gap-1.5">
      <span
        class={
          quiet
            ? 'text-sm font-medium text-slate-700 dark:text-slate-300'
            : 'text-[15px] font-semibold text-slate-900 dark:text-slate-100 tabular-nums'
        }
      >
        {value}
      </span>
      <span class="text-xs text-slate-500 dark:text-slate-400">{label}</span>
    </div>
  )
}
